// src/components/Admin/IVR.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  PlayArrow as PlayIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useAuthWithCustomerOverride } from '../../hooks/useAuthWithCustomerOverride';
import { useCustomerFilter } from '../../hooks/useCustomerFilter';
import errorLogger from '../../services/errorLogger.service';

// Create logger for this component
const logger = errorLogger.createComponentLogger('IVR');

interface IVRMenu {
  ivrID: number;
  CustID: string;
  Description: string;
  recordid?: number;
  recording_name?: string;
  times_to_repeat: number;
  pbxapp: number;
  pbxdesc?: string;
  pbxdigits?: string;
  Created: string;
  // Key mappings
  [key: string]: any;
}

interface Recording {
  RecordID: number;
  Name: string;
  Notes?: string;
}

interface OverflowTarget {
  value: string;
  name: string;
}

interface KeyMapping {
  key: string;
  app: number;
  digits: string;
  desc?: string;
  targetName?: string; // Store the display name of the target
}

const IVR: React.FC = () => {
  const authOverride = useAuthWithCustomerOverride();
  const { member, isLoginAsCustomer } = authOverride;
  const customerFilter = useCustomerFilter();
  
  // Check for login token on initialization
  const urlParams = new URLSearchParams(window.location.search);
  const hasLoginToken = urlParams.has('lat');
  const hasSessionData = !!sessionStorage.getItem('loginAsCustomer');
  const [waitingForLoginAs] = useState(() => {
    const shouldWait = hasLoginToken && !hasSessionData;
    if (shouldWait) {
      logger.debug('[IVR] Initializing with waiting=true due to login token');
    }
    return shouldWait;
  });
  
  const [loading, setLoading] = useState(true);
  const [ivrs, setIvrs] = useState<IVRMenu[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Dialog states
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedIvr, setSelectedIvr] = useState<IVRMenu | null>(null);
  
  // Key mapping dialog
  const [keyMappingDialogOpen, setKeyMappingDialogOpen] = useState(false);
  const [editingKey, setEditingKey] = useState<string>('');
  const [keyApp, setKeyApp] = useState(0);
  const [keyDigits, setKeyDigits] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    recordingId: '',
    timesToRepeat: 3,
    defaultApp: 9,
    defaultDigits: '0',
    defaultDesc: '',
  });
  
  // Key mappings state
  const [keyMappings, setKeyMappings] = useState<KeyMapping[]>([]);
  
  // Options data
  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [overflowActions, setOverflowActions] = useState<any[]>([]);
  const [overflowTargets, setOverflowTargets] = useState<OverflowTarget[]>([]);
  const [loadingTargets, setLoadingTargets] = useState(false);


  // Available keys for mapping
  const availableKeys = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '#'];

  useEffect(() => {
    if (!member) return;
    
    // Check for login token
    const urlParams = new URLSearchParams(window.location.search);
    const hasLoginToken = urlParams.has('lat');
    const sessionData = sessionStorage.getItem('loginAsCustomer');
    
    // If we have a login token but no session data, wait
    if (hasLoginToken && !sessionData) {
      logger.debug('[IVR] Have login token but no session data - waiting...');
      return;
    }
    
    // If session data exists, verify it matches member
    if (sessionData) {
      try {
        const parsed = JSON.parse(sessionData);
        if (parsed.customerId && parsed.customerId !== member.customerID) {
          logger.debug('[IVR] Session/member mismatch, waiting for sync...');
          return;
        }
      } catch (e) {
        logger.error('[IVR] Error parsing session data:', e);
      }
    }
    
    // Add small delay to let state settle
    const timer = setTimeout(() => {
      fetchIVRs();
      fetchOptions();
    }, 200);
    
    return () => clearTimeout(timer);
  }, [member?.customerID, isLoginAsCustomer]);

  // Listen for login-as-customer changes to update tab title and refetch data
  useEffect(() => {
    const handleLoginAsChange = (event: CustomEvent) => {
      if (event.detail && event.detail.customerId && event.detail.customerName) {
        document.title = `${event.detail.customerId} - ${event.detail.customerName} - IVR Menus`;
        // Also refetch data when login-as changes
        setTimeout(() => {
          if (member) {
            fetchIVRs();
            fetchOptions();
          }
        }, 100);
      }
    };

    window.addEventListener('loginAsCustomerChanged', handleLoginAsChange as EventListener);

    // Set initial title if in login-as mode
    if (isLoginAsCustomer && member) {
      const customerId = (member as any).customerID || (member as any).customer_id;
      const customerName = (member as any).loginAsCustomerName || '';
      if (customerId && customerName) {
        document.title = `${customerId} - ${customerName} - IVR Menus`;
      }
    }

    return () => {
      window.removeEventListener('loginAsCustomerChanged', handleLoginAsChange as EventListener);
      // Reset title when component unmounts
      if (!isLoginAsCustomer) {
        document.title = 'Portal';
      }
    };
  }, [isLoginAsCustomer, member]);

  // Fetch targets when dialog opens with a default action that requires targets
  useEffect(() => {
    if ((addDialogOpen || editDialogOpen) && formData.defaultApp && overflowActions.length > 0) {
      logger.debug('Dialog opened, checking if targets needed for defaultApp:', formData.defaultApp);
      fetchOverflowTargets(formData.defaultApp);
    }
  }, [addDialogOpen, editDialogOpen, formData.defaultApp, overflowActions]);

  const fetchIVRs = async () => {
    // SAFETY CHECK: Don't fetch if waiting for login-as data
    if (waitingForLoginAs) {
      logger.debug('[IVR] Still waiting for login-as data, aborting fetch');
      return;
    }
    
    // Verify session data matches member if in login-as mode
    const sessionData = sessionStorage.getItem('loginAsCustomer');
    if (sessionData) {
      try {
        const parsed = JSON.parse(sessionData);
        if (parsed.customerId && parsed.customerId !== member?.customerID) {
          logger.error('[IVR] CRITICAL: Customer ID mismatch!', {
            sessionCustomerId: parsed.customerId,
            memberCustomerId: member?.customerID
          });
          setError('Customer context mismatch - please refresh the page');
          setLoading(false);
          return;
        }
      } catch (e) {
        logger.error('[IVR] Error parsing session data:', e);
      }
    }
    
    setLoading(true);
    try {
      const params: any = {};
      
      // Add customer filter for master admins and reseller admins
      if (isLoginAsCustomer && member?.customerID) {
        logger.debug('[IVR] LOGIN AS MODE - Using customerID:', member.customerID);
        params.customerId = member.customerID;
      } else if (customerFilter.isFiltered && (member?.memberType === 'master_admin' || member?.memberType === 'reseller_admin')) {
        logger.debug('[IVR] FILTER MODE - Using customerFilter:', customerFilter.customerId);
        params.customerId = customerFilter.customerId;
      } else {
        logger.debug('[IVR] NO FILTER - Will use JWT customer');
      }
      
      const response = await api.get('/endpoints/ivr', { params });
      if (response.data.success) {
        setIvrs(response.data.data);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load IVR menus');
    } finally {
      setLoading(false);
    }
  };

  const fetchOptions = async () => {
    try {
      const params: any = {};
      
      // Add customer filter for master admins and reseller admins
      if (isLoginAsCustomer && member?.customerID) {
        params.customerId = member.customerID;
      } else if (customerFilter.isFiltered && (member?.memberType === 'master_admin' || member?.memberType === 'reseller_admin')) {
        params.customerId = customerFilter.customerId;
      }
      
      // Fetch recordings
      const recordingsResponse = await api.get('/endpoints/prompts/recordings', { params });
      if (recordingsResponse.data.success) {
        setRecordings(recordingsResponse.data.data.data || []);
      }

      // Fetch overflow actions from ring groups endpoint
      const actionsResponse = await api.get('/endpoints/ring_groups_v2/overflow-actions', { params });
      if (actionsResponse.data.success) {
        const actions = actionsResponse.data.data || [];
        setOverflowActions(actions);
      }
    } catch (err: any) {
      logger.error('Failed to fetch options:', err.message || err);
      // Set default overflow actions if the API fails
      const defaultActions = [
        { action_key: 0, action_name: 'Not Used', requires_target: false },
        { action_key: 1, action_name: 'Extension Group', requires_target: true },
        { action_key: 4, action_name: 'Extension', requires_target: true },
        { action_key: 7, action_name: 'Voicemail', requires_target: true },
        { action_key: 9, action_name: 'End Call', requires_target: false },
        { action_key: 10, action_name: 'Repeat Prompt', requires_target: false },
        { action_key: 11, action_name: 'Play Announcement', requires_target: true },
        { action_key: 15, action_name: 'Directory', requires_target: false },
        { action_key: 16, action_name: 'IVR', requires_target: true },
        { action_key: 17, action_name: 'Direct to Voicemail', requires_target: true },
        { action_key: 18, action_name: 'Voicemail Access', requires_target: false },
        { action_key: 19, action_name: 'Conference', requires_target: true },
        { action_key: 20, action_name: 'Consecutive Dial', requires_target: true },
      ];
      setOverflowActions(defaultActions);
    }
  };

  const fetchOverflowTargets = async (appId: number) => {
    logger.debug('fetchOverflowTargets called with appId:', appId);
    logger.debug('Available overflow actions:', overflowActions);
    
    // SAFETY CHECK: Don't fetch if waiting for login-as data
    if (waitingForLoginAs) {
      logger.debug('[IVR] Still waiting for login-as data, aborting overflow targets fetch');
      return;
    }
    
    // Find the action to check if it requires a target
    const action = overflowActions.find(a => a.action_key === appId);
    logger.debug('Found action:', action);
    
    if (!action || !action.requires_target) {
      logger.debug('Action does not require target or not found');
      setOverflowTargets([]);
      return;
    }

    setLoadingTargets(true);
    try {
      const params: any = {};
      
      // Add customer filter for master admins and reseller admins
      if (isLoginAsCustomer && member?.customerID) {
        params.customerId = member.customerID;
      } else if (customerFilter.isFiltered && (member?.memberType === 'master_admin' || member?.memberType === 'reseller_admin')) {
        params.customerId = customerFilter.customerId;
      }
      
      logger.debug(`Fetching targets from: /endpoints/ring_groups_v2/overflow-targets/${appId}`);
      const response = await api.get(`/endpoints/ring_groups_v2/overflow-targets/${appId}`, { params });
      logger.debug('Overflow targets response:', response.data);
      
      if (response.data.success) {
        setOverflowTargets(response.data.data || []);
      }
    } catch (err) {
      logger.error('Failed to fetch overflow targets:', err);
      setOverflowTargets([]);
    } finally {
      setLoadingTargets(false);
    }
  };

  const handleAdd = () => {
    // Ensure we have overflow actions loaded before opening dialog
    if (overflowActions.length === 0) {
      fetchOptions();
    }
    
    setFormData({
      name: '',
      recordingId: '',
      timesToRepeat: 3,
      defaultApp: 9,
      defaultDigits: '0',
      defaultDesc: '',
    });
    setKeyMappings([]);
    setSelectedIvr(null);
    setAddDialogOpen(true);
  };

  const handleEdit = async (ivr: IVRMenu) => {
    // Ensure we have overflow actions loaded before opening dialog
    if (overflowActions.length === 0) {
      fetchOptions();
    }
    
    setSelectedIvr(ivr);
    
    // Build key mappings from IVR data
    const mappings: KeyMapping[] = [];
    
    for (let i = 0; i <= 9; i++) {
      const app = ivr[`press_${i}_app`];
      if (app && app !== 0) {
        mappings.push({
          key: i.toString(),
          app,
          digits: ivr[`press_${i}_digits`] || '0',
          desc: ivr[`press_${i}_desc`] || '',
          // For now, just use the digits as targetName - we'll update this after fetching targets
          targetName: ivr[`press_${i}_digits`] || '0',
        });
      }
    }
    
    // Add star - Note: star key doesn't have app field in DB, only digits
    if (ivr.press_star_digits && ivr.press_star_digits !== '0') {
      mappings.push({
        key: '*',
        app: 9, // Default to extension for star key
        digits: ivr.press_star_digits || '0',
        desc: ivr.press_star_desc || '',
        targetName: ivr.press_star_digits || '0',
      });
    }
    
    if (ivr.press_pound_app && ivr.press_pound_app !== 0) {
      mappings.push({
        key: '#',
        app: ivr.press_pound_app,
        digits: ivr.press_pound_digits || '0',
        desc: ivr.press_pound_desc || '',
        targetName: ivr.press_pound_digits || '0',
      });
    }
    
    setKeyMappings(mappings);
    setFormData({
      name: ivr.Description,
      recordingId: ivr.recordid?.toString() || '',
      timesToRepeat: ivr.times_to_repeat,
      defaultApp: ivr.pbxapp,
      defaultDigits: ivr.pbxdigits || '0',
      defaultDesc: ivr.pbxdesc || '',
    });
    
    // Fetch targets for default action
    fetchOverflowTargets(ivr.pbxapp);
    
    // Fetch target names for all key mappings
    // We'll use a simplified approach - just show the IVR names which we already have
    const updatedMappings = mappings.map(mapping => {
      if (mapping.app === 16 && mapping.digits !== '0') {
        // For IVR targets, we can look up the name from our loaded IVRs
        const targetIvr = ivrs.find(i => i.ivrID.toString() === mapping.digits);
        if (targetIvr) {
          return { ...mapping, targetName: targetIvr.Description };
        }
      }
      return mapping;
    });
    
    setKeyMappings(updatedMappings);
    setEditDialogOpen(true);
  };

  const handleDelete = (ivr: IVRMenu) => {
    setSelectedIvr(ivr);
    setDeleteDialogOpen(true);
  };

  const handleAddKeyMapping = () => {
    const unusedKeys = availableKeys.filter(key => 
      !keyMappings.find(m => m.key === key)
    );
    
    if (unusedKeys.length > 0) {
      setEditingKey(unusedKeys[0]);
      setKeyApp(0);
      setKeyDigits('0');
      setKeyMappingDialogOpen(true);
    }
  };

  const handleSaveKeyMapping = () => {
    if (editingKey) {
      const existingIndex = keyMappings.findIndex(m => m.key === editingKey);

      // Find the target name from overflowTargets
      let targetName = overflowTargets.find(t => t.value === keyDigits)?.name || keyDigits;

      // For AI Agent (app 25), prepend custid-agentname format for Asterisk
      if (keyApp === 25) {
        const effectiveCustomerId = isLoginAsCustomer && member?.customerID
          ? member.customerID
          : customerFilter.isFiltered && customerFilter.customerId
            ? customerFilter.customerId
            : member?.customerID;
        targetName = `${effectiveCustomerId}-${targetName}`;
      }

      if (existingIndex >= 0) {
        // Update existing
        const newMappings = [...keyMappings];
        newMappings[existingIndex] = {
          key: editingKey,
          app: keyApp,
          digits: keyDigits,
          desc: targetName,
          targetName: targetName,
        };
        setKeyMappings(newMappings);
      } else {
        // Add new
        setKeyMappings([...keyMappings, {
          key: editingKey,
          app: keyApp,
          digits: keyDigits,
          desc: targetName,
          targetName: targetName,
        }]);
      }

      setKeyMappingDialogOpen(false);
    }
  };

  const handleEditKeyMapping = (mapping: KeyMapping) => {
    setEditingKey(mapping.key);
    setKeyApp(mapping.app);
    setKeyDigits(mapping.digits);
    fetchOverflowTargets(mapping.app);
    setKeyMappingDialogOpen(true);
  };

  const handleDeleteKeyMapping = (key: string) => {
    setKeyMappings(keyMappings.filter(m => m.key !== key));
  };

  const handleSaveIVR = async () => {
    logger.debug('handleSaveIVR called');
    logger.debug('Form data:', formData);
    logger.debug('Key mappings:', keyMappings);
    
    try {
      setError(null); // Clear any previous errors
      
      // Convert key mappings array to object format for API
      // Backend expects numeric indices for keys 0-9
      const keyMappingsObj: any = {};
      keyMappings.forEach(mapping => {
        // Convert key to proper format - backend expects numeric keys
        const key = mapping.key === '*' ? 'star' : mapping.key === '#' ? 'pound' : mapping.key;
        keyMappingsObj[key] = {
          app: mapping.app,
          digits: mapping.digits,
          desc: mapping.desc || '',
        };
      });

      const data = {
        name: formData.name,
        recording_id: formData.recordingId || null,
        times_to_repeat: formData.timesToRepeat,
        default_app: formData.defaultApp,
        default_digits: formData.defaultDigits,
        default_desc: formData.defaultDesc,
        key_mappings: keyMappingsObj,
      };
      
      logger.debug('Prepared data for API:', data);
      
      // Prepare params for customer filtering
      const params: any = {};
      if (isLoginAsCustomer && member?.customerID) {
        params.customerId = member.customerID;
      } else if (customerFilter.isFiltered && (member?.memberType === 'master_admin' || member?.memberType === 'reseller_admin')) {
        params.customerId = customerFilter.customerId;
      }
      
      if (selectedIvr) {
        // Update existing IVR
        logger.debug(`Updating IVR: ${selectedIvr.ivrID}`, { data, params });
        const response = await api.put(`/endpoints/ivr/${selectedIvr.ivrID}`, data, { params });
        logger.debug('Update response:', response);
        
        // Always close dialog and refresh on successful response
        setSuccess('IVR menu updated successfully');
        // Close dialog properly
        setEditDialogOpen(false);
        setAddDialogOpen(false);
        setSelectedIvr(null);
        // Reset form
        setFormData({
          name: '',
          recordingId: '',
          timesToRepeat: 3,
          defaultApp: 9,
          defaultDigits: '0',
          defaultDesc: '',
        });
        setKeyMappings([]);
        // Add small delay before fetching to ensure backend processing is complete
        setTimeout(() => {
          fetchIVRs();
        }, 100);
      } else {
        // Create new IVR
        logger.debug('Creating new IVR', { data, params });
        const response = await api.post('/endpoints/ivr', data, { params });
        logger.debug('Create response:', response);
        
        // Always close dialog and refresh on successful response
        setSuccess('IVR menu created successfully');
        // Close dialog properly
        setAddDialogOpen(false);
        setEditDialogOpen(false);
        setSelectedIvr(null);
        // Reset form
        setFormData({
          name: '',
          recordingId: '',
          timesToRepeat: 3,
          defaultApp: 9,
          defaultDigits: '0',
          defaultDesc: '',
        });
        setKeyMappings([]);
        // Add small delay before fetching to ensure backend processing is complete
        setTimeout(() => {
          fetchIVRs();
        }, 100);
      }
    } catch (err: any) {
      logger.error('Save IVR error:', err);
      logger.error('Error response:', err.response);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to save IVR menu';
      setError(errorMessage);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedIvr) return;

    try {
      // Add customer filter params if needed
      const params: any = {};
      if (isLoginAsCustomer && member?.customerID) {
        params.customerId = member.customerID;
      } else if (customerFilter.isFiltered && (member?.memberType === 'master_admin' || member?.memberType === 'reseller_admin')) {
        params.customerId = customerFilter.customerId;
      }
      
      logger.debug(`Deleting IVR: ${selectedIvr.ivrID}`, { params });
      
      const response = await api.delete(`/endpoints/ivr/${selectedIvr.ivrID}`, { params });
      
      logger.debug('Delete response:', response);
      
      // Always process as successful
      setSuccess('IVR menu deleted successfully');
      setDeleteDialogOpen(false);
      
      // Optimistically remove the IVR from the list
      setIvrs(prevIvrs => prevIvrs.filter(ivr => ivr.ivrID !== selectedIvr.ivrID));
      
      setSelectedIvr(null);
      
      // Still fetch to ensure consistency with backend
      setTimeout(() => {
        fetchIVRs();
      }, 500);
      
    } catch (err: any) {
      logger.error('Delete error:', err);
      setError(err.response?.data?.message || 'Failed to delete IVR menu');
      setDeleteDialogOpen(false);
    }
  };

  const handlePlayGreeting = async (ivr: IVRMenu) => {
    if (!ivr.recordid) return;

    try {
      // Use api service to get the correct base URL
      const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
      const apiUrl = process.env.REACT_APP_API_URL || '/api/v1';
      const audioUrl = `${apiUrl}/endpoints/ivr/${ivr.ivrID}/greeting-audio`;

      const audio = new Audio();

      const response = await fetch(audioUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to load audio');
      }
      
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      
      audio.src = blobUrl;
      audio.play();
      
      audio.addEventListener('ended', () => {
        URL.revokeObjectURL(blobUrl);
      });
    } catch (error) {
      logger.error('Failed to play greeting:', error);
      setError('Failed to play greeting audio');
    }
  };

  const getActionName = (actionKey: number) => {
    const action = overflowActions.find(a => a.action_key === actionKey);
    return action ? action.action_name : 'Unknown';
  };

  const getKeyDisplayName = (key: string) => {
    switch (key) {
      case '*': return 'Star (*)';
      case '#': return 'Pound (#)';
      default: return key;
    }
  };

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4" component="h1">
            IVR Menu Management
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchIVRs}
              disabled={loading}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAdd}
            >
              Add IVR Menu
            </Button>
          </Box>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>IVR Name</TableCell>
              <TableCell>Greeting</TableCell>
              <TableCell align="center">Repeat</TableCell>
              <TableCell>Default Action</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : ivrs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No IVR menus found
                </TableCell>
              </TableRow>
            ) : (
              ivrs.map((ivr) => (
                <TableRow key={ivr.ivrID}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {ivr.Description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {ivr.recording_name ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                          {ivr.recording_name}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => handlePlayGreeting(ivr)}
                          title="Play greeting"
                        >
                          <PlayIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No greeting
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={`${ivr.times_to_repeat}x`} size="small" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {(() => {
                        const action = overflowActions.find(a => a.action_key === ivr.pbxapp);
                        const actionName = action?.action_name || 'Unknown';
                        
                        // Add target display for actions that require targets
                        if (action?.requires_target && ivr.pbxdigits && ivr.pbxdigits !== '0') {
                          // Special handling for IVR targets
                          if (ivr.pbxapp === 16) {
                            const targetIvr = ivrs.find(i => i.ivrID.toString() === ivr.pbxdigits);
                            return `${actionName} → ${targetIvr?.Description || ivr.pbxdigits}`;
                          }
                          // Use pbxdesc (friendly name) if available, otherwise use pbxdigits (ID)
                          return `${actionName} → ${ivr.pbxdesc || ivr.pbxdigits}`;
                        }
                        
                        return actionName;
                      })()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(ivr)}
                      title="Edit IVR"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(ivr)}
                      title="Delete IVR"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog 
        open={addDialogOpen || editDialogOpen} 
        onClose={() => {
          setAddDialogOpen(false);
          setEditDialogOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ py: 1.5 }}>
          <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
            {selectedIvr ? 'Edit IVR Menu' : 'Add New IVR Menu'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Box sx={{ mb: 1.5, mt: 1 }}>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="IVR Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={8}>
                <FormControl fullWidth size="small">
                  <InputLabel>Greeting</InputLabel>
                  <Select
                    value={formData.recordingId}
                    onChange={(e) => setFormData({ ...formData, recordingId: e.target.value })}
                    label="Greeting"
                  >
                    <MenuItem value="">No Recording</MenuItem>
                    {recordings.map((rec) => (
                      <MenuItem key={rec.RecordID} value={rec.RecordID}>
                        {rec.Name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Repeat</InputLabel>
                  <Select
                    value={formData.timesToRepeat}
                    onChange={(e) => setFormData({ ...formData, timesToRepeat: Number(e.target.value) })}
                    label="Repeat"
                  >
                    {[1, 2, 3, 4, 5].map((num) => (
                      <MenuItem key={num} value={num}>{num} times</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            
            <Box sx={{ mt: 1 }}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Default Action</InputLabel>
                    <Select
                      value={formData.defaultApp}
                      onChange={(e) => {
                        const appId = Number(e.target.value);
                        setFormData({ ...formData, defaultApp: appId, defaultDigits: '0' });
                        fetchOverflowTargets(appId);
                      }}
                      label="Default Action"
                    >
                      {overflowActions.length > 0 ? (
                        overflowActions.map((action) => (
                          <MenuItem key={action.action_key} value={action.action_key}>
                            {action.action_name}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem disabled>Loading actions...</MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  {overflowActions.find(a => a.action_key === formData.defaultApp)?.requires_target ? (
                    loadingTargets ? (
                      <FormControl fullWidth size="small">
                        <InputLabel>Target</InputLabel>
                        <Select value="" disabled label="Target">
                          <MenuItem value="">Loading...</MenuItem>
                        </Select>
                      </FormControl>
                    ) : overflowTargets.length > 0 ? (
                      <FormControl fullWidth size="small">
                        <InputLabel>Target</InputLabel>
                        <Select
                          value={formData.defaultDigits}
                          onChange={(e) => {
                            const selectedTarget = overflowTargets.find(t => t.value === e.target.value);
                            // For AI Agent (app 25), save custid-agentname format for Asterisk
                            let descValue = selectedTarget?.name || '';
                            if (formData.defaultApp === 25 && selectedTarget?.name) {
                              const effectiveCustomerId = isLoginAsCustomer && member?.customerID
                                ? member.customerID
                                : customerFilter.isFiltered && customerFilter.customerId
                                  ? customerFilter.customerId
                                  : member?.customerID;
                              descValue = `${effectiveCustomerId}-${selectedTarget.name}`;
                            }
                            setFormData({
                              ...formData,
                              defaultDigits: e.target.value,
                              defaultDesc: descValue
                            });
                          }}
                          label="Target"
                        >
                          {overflowTargets.map((target) => (
                            <MenuItem key={target.value} value={target.value}>
                              {target.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <FormControl fullWidth size="small">
                        <InputLabel>Target</InputLabel>
                        <Select value="" disabled label="Target">
                          <MenuItem value="">
                            {(() => {
                              const actionName = overflowActions.find(a => a.action_key === formData.defaultApp)?.action_name || '';
                              if (actionName.includes('Ring Group')) return 'No ring groups available. Please create a ring group first.';
                              if (actionName.includes('Queue')) return 'No queues available. Please create a queue first.';
                              if (actionName.includes('Extension')) return 'No extensions available. Please create an extension first.';
                              if (actionName.includes('Voicemail')) return 'No voicemail boxes available.';
                              if (actionName.includes('IVR')) return 'No IVR menus available. Please create an IVR menu first.';
                              return 'No targets available for this action.';
                            })()}
                          </MenuItem>
                        </Select>
                      </FormControl>
                    )
                  ) : null}
                </Grid>
              </Grid>
            </Box>
          </Box>

          {/* Key Mappings */}
          <Divider sx={{ mb: 1 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2">Key Mappings</Typography>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddKeyMapping}
              disabled={keyMappings.length >= availableKeys.length}
              sx={{ minHeight: 28 }}
            >
              Add Key
            </Button>
          </Box>
          
          {keyMappings.length === 0 ? (
            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
              No key mappings configured. Click "Add Key" to add one.
            </Typography>
          ) : (
            <List dense sx={{ py: 0 }}>
              {keyMappings.sort((a, b) => {
                // Sort by key: 0-9 first, then *, then #
                const order = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '#'];
                return order.indexOf(a.key) - order.indexOf(b.key);
              }).map((mapping) => (
                <ListItem key={mapping.key} divider sx={{ py: 0.5, px: 1 }}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip label={`${mapping.key}`} size="small" sx={{ minWidth: 32, height: 24 }} />
                        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                          {getActionName(mapping.app)}
                          {mapping.digits && mapping.digits !== '0' && ` → ${mapping.targetName || mapping.digits}`}
                        </Typography>
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={() => handleEditKeyMapping(mapping)}
                      sx={{ p: 0.5 }}
                    >
                      <EditIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={() => handleDeleteKeyMapping(mapping.key)}
                      sx={{ p: 0.5 }}
                    >
                      <DeleteIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setAddDialogOpen(false);
            setEditDialogOpen(false);
          }}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveIVR} 
            variant="contained"
            disabled={!formData.name || formData.name.trim() === ''}
          >
            {selectedIvr ? 'Update' : 'Create'} IVR Menu
          </Button>
        </DialogActions>
      </Dialog>

      {/* Key Mapping Dialog */}
      <Dialog
        open={keyMappingDialogOpen}
        onClose={() => setKeyMappingDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {keyMappings.find(m => m.key === editingKey) ? 'Edit' : 'Add'} Key Mapping
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Key</InputLabel>
                <Select
                  value={editingKey}
                  onChange={(e) => setEditingKey(e.target.value)}
                  label="Key"
                  disabled={!!keyMappings.find(m => m.key === editingKey)}
                >
                  {availableKeys
                    .filter(key => !keyMappings.find(m => m.key === key) || key === editingKey)
                    .map((key) => (
                      <MenuItem key={key} value={key}>
                        Press {getKeyDisplayName(key)}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Action</InputLabel>
                <Select
                  value={keyApp}
                  onChange={(e) => {
                    const appId = Number(e.target.value);
                    setKeyApp(appId);
                    setKeyDigits('0');
                    fetchOverflowTargets(appId);
                  }}
                  label="Action"
                >
                  {overflowActions.map((action) => (
                    <MenuItem key={action.action_key} value={action.action_key}>
                      {action.action_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {overflowActions.find(a => a.action_key === keyApp)?.requires_target ? (
              <Grid item xs={12}>
                {loadingTargets ? (
                  <FormControl fullWidth size="small">
                    <InputLabel>Target</InputLabel>
                    <Select value="" disabled label="Target">
                      <MenuItem value="">Loading...</MenuItem>
                    </Select>
                  </FormControl>
                ) : overflowTargets.length > 0 ? (
                  <FormControl fullWidth size="small">
                    <InputLabel>Target</InputLabel>
                    <Select
                      value={keyDigits}
                      onChange={(e) => setKeyDigits(e.target.value)}
                      label="Target"
                    >
                      {overflowTargets.map((target) => (
                        <MenuItem key={target.value} value={target.value}>
                          {target.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <FormControl fullWidth size="small">
                    <InputLabel>Target</InputLabel>
                    <Select value="" disabled label="Target">
                      <MenuItem value="">
                        {(() => {
                          const actionName = overflowActions.find(a => a.action_key === keyApp)?.action_name || '';
                          if (actionName.includes('Ring Group')) return 'No ring groups available. Please create a ring group first.';
                          if (actionName.includes('Queue')) return 'No queues available. Please create a queue first.';
                          if (actionName.includes('Extension')) return 'No extensions available. Please create an extension first.';
                          if (actionName.includes('Voicemail')) return 'No voicemail boxes available.';
                          if (actionName.includes('IVR')) return 'No IVR menus available. Please create an IVR menu first.';
                          return 'No targets available for this action.';
                        })()}
                      </MenuItem>
                    </Select>
                  </FormControl>
                )}
              </Grid>
            ) : null}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKeyMappingDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveKeyMapping} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => {
        setDeleteDialogOpen(false);
        setSelectedIvr(null);
      }}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the IVR menu "{selectedIvr?.Description}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDeleteDialogOpen(false);
            setSelectedIvr(null);
          }}>Cancel</Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            disabled={!selectedIvr}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default IVR;